import {
  Box,
  Button,
  Card,
  Checkbox,
  CrudMode,
  DataArray,
  DataDisplay,
  formatBoolean,
  Grid,
  GridBreak,
  pn,
  Stack,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { Input, Select } from "components";
import {
  School,
  StudentServiceType,
  StudentServiceTypeEditResource,
  StudentServiceTypeViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";

export const BasicInfo = (props: {
  crudMode: CrudMode;
  serviceType: StudentServiceType;
  setServiceType: (value: StudentServiceType) => void;
  viewResource: StudentServiceTypeViewResource;
  editResource: StudentServiceTypeEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const nameField = () => {
    const propertyName = pn<StudentServiceType>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.serviceType.name}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.serviceType.name}
        onChange={(e) =>
          props.setServiceType(
            produce(props.serviceType, (draft) => {
              draft.name = e.target.value;
            })
          )
        }
      />
    );
  };

  const schoolsField = () => {
    const propertyName = pn<StudentServiceType>("schools");
    const property = properties.find((x) => x.propertyName === propertyName);
    const getSchoolLabel = (school: School) => (
      <Stack direction="row" spacing="sm" alignItems={"center"}>
        <Typography variant="inherit">{school.name}</Typography>
        <Typography variant="caption" color={(theme) => theme.palette.warning.main}>
          {school.inactive && "Inactive"}
        </Typography>
      </Stack>
    );

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.serviceType.schools?.map((x) => getSchoolLabel(x))} />
      </DataDisplay>
    ) : (
      <Stack alignItems="flex-start">
        <Select
          multiple
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.serviceType.schools}
          options={props.editResource.schools}
          getOptionLabel={(option) => option.name}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              {getSchoolLabel(option)}
            </Box>
          )}
          onChange={(e, values) =>
            props.setServiceType(
              produce(props.serviceType, (draft) => {
                draft.schools = values;
              })
            )
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
        <Button
          onClick={() =>
            props.setServiceType(
              produce(props.serviceType, (draft) => {
                draft.schools = props.editResource.schools;
              })
            )
          }
          size="small"
          textTransform="none"
        >
          + Select all schools
        </Button>
      </Stack>
    );
  };

  const isForParentField = () => {
    const propertyName = pn<StudentServiceType>("isForParent");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.serviceType.isForParent)}</DataDisplay>
    ) : (
      <Stack height="100%" justifyContent={"center"}>
        <Checkbox
          label={property?.displayName}
          checked={props.serviceType.isForParent}
          onChange={(e) =>
            props.setServiceType(
              produce(props.serviceType, (draft) => {
                draft.isForParent = e.target.checked;
                draft.aprCategoryId = undefined;
              })
            )
          }
        />
      </Stack>
    );
  };

  const isNameMandatoryField = () => {
    const propertyName = pn<StudentServiceType>("isNameMandatory");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.serviceType.isNameMandatory)}</DataDisplay>
    ) : (
      <Stack height="100%" justifyContent={"center"}>
        <Checkbox
          label={property?.displayName}
          checked={props.serviceType.isNameMandatory}
          onChange={(e) =>
            props.setServiceType(
              produce(props.serviceType, (draft) => {
                draft.isNameMandatory = e.target.checked;
              })
            )
          }
        />
      </Stack>
    );
  };

  const isModalityMandatoryField = () => {
    const propertyName = pn<StudentServiceType>("isModalityMandatory");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.serviceType.isModalityMandatory)}</DataDisplay>
    ) : (
      <Stack height="100%" justifyContent={"center"}>
        <Checkbox
          label={property?.displayName}
          checked={props.serviceType.isModalityMandatory}
          onChange={(e) =>
            props.setServiceType(
              produce(props.serviceType, (draft) => {
                draft.isModalityMandatory = e.target.checked;
              })
            )
          }
        />
      </Stack>
    );
  };

  const isServiceLocationMandatoryField = () => {
    const propertyName = pn<StudentServiceType>("isServiceLocationMandatory");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {formatBoolean(props.serviceType.isServiceLocationMandatory)}
      </DataDisplay>
    ) : (
      <Stack height="100%" justifyContent={"center"}>
        <Checkbox
          label={property?.displayName}
          checked={props.serviceType.isServiceLocationMandatory}
          onChange={(e) =>
            props.setServiceType(
              produce(props.serviceType, (draft) => {
                draft.isServiceLocationMandatory = e.target.checked;
              })
            )
          }
        />
      </Stack>
    );
  };

  const isProviderMandatoryField = () => {
    const propertyName = pn<StudentServiceType>("isProviderMandatory");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.serviceType.isProviderMandatory)}</DataDisplay>
    ) : (
      <Stack height="100%" justifyContent={"center"}>
        <Checkbox
          label={property?.displayName}
          checked={props.serviceType.isProviderMandatory}
          onChange={(e) =>
            props.setServiceType(
              produce(props.serviceType, (draft) => {
                draft.isProviderMandatory = e.target.checked;
              })
            )
          }
        />
      </Stack>
    );
  };

  const inactiveField = () => {
    const propertyName = pn<StudentServiceType>("inactive");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.serviceType.inactive)}</DataDisplay>
    ) : (
      <Stack height="100%" justifyContent={"center"}>
        <Checkbox
          label={property?.displayName}
          checked={props.serviceType.inactive}
          onChange={(e) =>
            props.setServiceType(
              produce(props.serviceType, (draft) => {
                draft.inactive = e.target.checked;
              })
            )
          }
        />
      </Stack>
    );
  };

  const aprStudentServiceField = () => {
    const propertyName = pn<StudentServiceType>("aprStudentServiceId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.serviceType.aprStudentService?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.aprStudentServices?.find((x) => x.id === props.serviceType.aprStudentServiceId)}
        options={props.editResource.aprStudentServices?.filter((x) => x.isForParent === props.serviceType.isForParent)}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setServiceType(
            produce(props.serviceType, (draft) => {
              draft.aprStudentServiceId = value?.id;
            })
          )
        }
      />
    );
  };

  const apr2StudentServiceField = () => {
    const propertyName = pn<StudentServiceType>("apr2StudentServiceId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.serviceType.apr2StudentService?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.apr2StudentServices?.find((x) => x.id === props.serviceType.apr2StudentServiceId)}
        options={props.editResource.apr2StudentServices}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setServiceType(
            produce(props.serviceType, (draft) => {
              draft.apr2StudentServiceId = value?.id;
            })
          )
        }
      />
    );
  };

  const aprCategoryField = () => {
    const propertyName = pn<StudentServiceType>("aprCategoryId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.serviceType.aprCategory?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.aprCategories?.find((x) => x.id === props.serviceType.aprCategoryId)}
        options={props.editResource.aprCategories?.filter((x) => x.isForParent === props.serviceType.isForParent)}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setServiceType(
            produce(props.serviceType, (draft) => {
              draft.aprCategoryId = value?.id;
            })
          )
        }
      />
    );
  };

  const ccrecServiceTypeField = () => {
    const propertyName = pn<StudentServiceType>("ccrecServiceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.serviceType.ccrecServiceType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.ccrecServiceTypes?.find((x) => x.id === props.serviceType.ccrecServiceTypeId)}
        options={props.editResource.ccrecServiceTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setServiceType(
            produce(props.serviceType, (draft) => {
              draft.ccrecServiceTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Card>
      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={largeFieldBreakpoints}>
          {nameField()}
        </Grid>

        <GridBreak />

        <Grid item xs={largeFieldBreakpoints}>
          {schoolsField()}
        </Grid>

        <GridBreak />

        {/* <Grid item xs={smallFieldBreakpoints}>
          {aprStudentServiceField()}
        </Grid>

        {!props.serviceType.isForParent && (
          <Grid item xs={smallFieldBreakpoints}>
            {apr2StudentServiceField()}
          </Grid>
        )} */}

        <GridBreak />

        <Grid item xs={smallFieldBreakpoints}>
          {isForParentField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {isNameMandatoryField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {isProviderMandatoryField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {isModalityMandatoryField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {isServiceLocationMandatoryField()}
        </Grid>

        <GridBreak />

        <Grid item xs={smallFieldBreakpoints}>
          {aprCategoryField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {ccrecServiceTypeField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {inactiveField()}
        </Grid>
      </Grid>
    </Card>
  );
};
